import type { IconName } from '../components/AppIcon.vue'

// Navigation of the two shells. Ids are the production screen ids (assets/js/app.js VIEWS), which the legacy
// CSS also uses as body[data-view]. `to` is the Vue route; screens that are not built yet route to a placeholder.

export interface NavItem {
  id: string
  label: string
  to: string
  icon: IconName
  /** Route path prefixes that keep the item highlighted. */
  match: string[]
}

/** Desktop sidebar, group "备考主线". */
export const MAIN_NAV: NavItem[] = [
  { id: 'home', label: '主页', to: '/', icon: 'home', match: ['/'] },
  { id: 'map-home', label: '考点地图', to: '/map', icon: 'map', match: ['/map'] },
  { id: 'quiz', label: '笔试练习', to: '/quiz', icon: 'checklist', match: ['/quiz', '/practice', '/review', '/wrong', '/history'] },
  { id: 'interview-practice', label: '面试模拟盘', to: '/interview', icon: 'microphone', match: ['/interview'] },
  { id: 'membership', label: '会员中心', to: '/membership', icon: 'crown', match: ['/membership', '/shop'] },
  { id: 'me', label: '我的进度', to: '/me', icon: 'user', match: ['/me', '/login', '/register', '/reset-password'] },
]

/** Desktop sidebar, group "体验与共创". */
export const LAB_NAV: (NavItem & { status: string })[] = [
  { id: 'guide', label: '导游词库', to: '/guide-words', icon: 'document', match: ['/guide-words'], status: '体验中' },
]

/** Phone tab bar (assets/mobile-ui/shell.js mountTabs). */
export const TAB_NAV: NavItem[] = [
  { id: 'home', label: '首页', to: '/', icon: 'home', match: ['/', '/exam-guide', '/syllabus'] },
  { id: 'quiz', label: '题库', to: '/quiz', icon: 'clipboard', match: ['/quiz', '/practice', '/review', '/wrong', '/history'] },
  { id: 'interview-practice', label: '面试', to: '/interview', icon: 'mic', match: ['/interview'] },
  { id: 'map-home', label: '地图', to: '/map', icon: 'pin', match: ['/map'] },
  { id: 'me', label: '我的', to: '/me', icon: 'profile', match: ['/me', '/login', '/register', '/reset-password', '/membership', '/shop'] },
]

/** Production go('id') targets, for copy ported from the legacy markup. */
export const LEGACY_TARGETS: Record<string, string> = {
  home: '/',
  quiz: '/quiz',
  'map-home': '/map',
  'interview-practice': '/interview',
  membership: '/membership',
  me: '/me',
  guide: '/guide-words',
  video: '/video',
}

export function isActive(item: NavItem, path: string): boolean {
  return item.match.some(prefix => (prefix === '/' ? path === '/' : path === prefix || path.startsWith(prefix + '/')))
}

export interface ShellInfo {
  /** body[data-view] value, a production screen id. */
  view: string
  /** Page name in the desktop workspace bar. */
  title: string
  /** Content runs edge to edge (no page padding): map and the exam guide. */
  fullBleed: boolean
}

const VIEWS: { prefixes: string[]; info: ShellInfo }[] = [
  { prefixes: ['/map'], info: { view: 'map-home', title: '考点地图', fullBleed: true } },
  { prefixes: ['/exam-guide'], info: { view: 'exam-guide', title: '考试指南', fullBleed: true } },
  { prefixes: ['/syllabus'], info: { view: 'syllabus', title: '考试大纲', fullBleed: false } },
  { prefixes: ['/quiz', '/practice', '/review', '/wrong', '/history'], info: { view: 'quiz', title: '笔试练习', fullBleed: false } },
  { prefixes: ['/interview'], info: { view: 'interview-practice', title: '面试模拟盘', fullBleed: false } },
  { prefixes: ['/membership'], info: { view: 'membership', title: '会员中心', fullBleed: false } },
  { prefixes: ['/shop'], info: { view: 'shop', title: '学霸商城', fullBleed: false } },
  { prefixes: ['/me', '/login', '/register', '/reset-password'], info: { view: 'me', title: '我的进度', fullBleed: false } },
  { prefixes: ['/guide-words'], info: { view: 'guide', title: '导游词库', fullBleed: false } },
  { prefixes: ['/video'], info: { view: 'video', title: '学习资源', fullBleed: false } },
]

export function shellInfo(path: string): ShellInfo {
  if (path === '/') return { view: 'home', title: '主页', fullBleed: false }
  const hit = VIEWS.find(v => v.prefixes.some(p => path === p || path.startsWith(p + '/')))
  return hit?.info ?? { view: 'home', title: '导游学霸', fullBleed: false }
}
